import { useEffect, useRef, useState } from 'react';

import { ApiError } from 'src/api/client';
import { fetchClipAudio } from 'src/api/endpoints';
import { Button } from 'src/components/ui/Button';

/**
 * Plays one training clip.
 *
 * The endpoint requires a token and an `<audio src>` carries no headers, so the bytes are fetched
 * and handed to the element as an object URL. That trades a `src` attribute for a fetch, which is
 * why it stays behind a button: a profile page draws up to sixty clips, and `preload="none"` used
 * to mean none of them cost anything until an editor pressed play. Loading them all on render to
 * keep the markup simpler would download tens of megabytes of someone's voice to draw a list.
 */
export function ClipAudio({ clipId }: { clipId: string }) {
  const [source, setSource] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const objectUrl = useRef<string | null>(null);

  // The browser keeps the blob alive until it is revoked, so an unmounted player must let go of it.
  useEffect(
    () => () => {
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    },
    []
  );

  if (error) return <p className="clip__text clip__text--absent">{error}</p>;

  if (!source) {
    return (
      <Button
        variant="ghost"
        busy={loading}
        onClick={async () => {
          setLoading(true);
          try {
            const url = await fetchClipAudio(clipId);
            objectUrl.current = url;
            setSource(url);
          } catch (cause: unknown) {
            setError(cause instanceof ApiError ? `No se pudo cargar el audio: ${cause.message}` : 'No se pudo cargar el audio.');
          } finally {
            setLoading(false);
          }
        }}
      >
        Escuchar
      </Button>
    );
  }

  // autoPlay because the editor already pressed a button to get here; a second press to start it
  // would be the same click twice. onError covers the fetch succeeding but the bytes not decoding
  // as audio — an HTTP 200 with a corrupt body never reaches the catch above.
  return (
    <audio className="clip__audio" controls autoPlay src={source} onError={() => setError('El audio llegó pero no se pudo decodificar.')}>
      Tu navegador no puede reproducir este audio.
    </audio>
  );
}
